/** @file Mutations as reversible commands. All state writes flow through here. */
import { appStore } from "./state.js";
import { shortId } from "./ids.js";

/** @typedef {import("./history.js").Command} Command */
/** @typedef {import("./play.js").Token} Token */
/** @typedef {import("./play.js").Drawing} Drawing */
/** @typedef {import("./play.js").ToolMode} ToolMode */

/**
 * @param {string} tokenId
 * @param {{x:number,y:number}} from
 * @param {{x:number,y:number}} to
 * @returns {Command}
 */
export function moveTokenCommand(tokenId, from, to) {
  return {
    type: "moveToken",
    do: () => {
      updateToken(tokenId, () => ({ x: to.x, y: to.y }));
      writeTokenPosToActiveScene(tokenId, to);
    },
    undo: () => {
      updateToken(tokenId, () => ({ x: from.x, y: from.y }));
      writeTokenPosToActiveScene(tokenId, from);
    },
  };
}

/**
 * Mirror a token position change into the active scene so scene switches stay consistent.
 * Ball-kind tokens write to `scenes[active].ball`; others write to `scenes[active].tokens[id]`.
 * @param {string} tokenId
 * @param {{x:number,y:number}} pos
 */
function writeTokenPosToActiveScene(tokenId, pos) {
  appStore.set((state) => {
    const idx = state.activeSceneIndex;
    const active = state.scenes[idx];
    if (!active) {
      return {};
    }
    const tok = state.tokens.find((t) => t.id === tokenId);
    if (!tok) {
      return {};
    }
    /** @type {import("./play.js").Scene} */
    let patched;
    if (tok.kind === "ball") {
      patched = { ...active, ball: { x: pos.x, y: pos.y } };
    } else {
      patched = {
        ...active,
        tokens: { ...active.tokens, [tokenId]: { x: pos.x, y: pos.y } },
      };
    }
    return {
      scenes: state.scenes.map((sc, i) => (i === idx ? patched : sc)),
    };
  });
}

/**
 * @param {string} tokenId
 * @param {Partial<Pick<Token, "number" | "label">>} from
 * @param {Partial<Pick<Token, "number" | "label">>} to
 * @returns {Command}
 */
export function updateTokenPropsCommand(tokenId, from, to) {
  return {
    type: "updateTokenProps",
    do: () => updateToken(tokenId, () => to),
    undo: () => updateToken(tokenId, () => from),
  };
}

/**
 * @param {Omit<Drawing, "id">} spec
 * @returns {Command}
 */
export function addDrawingCommand(spec) {
  const id = shortId();
  /** @type {Drawing} */
  const drawing = { ...spec, id };
  const activeIdx = appStore.get().activeSceneIndex;
  return {
    type: "addDrawing",
    do: () =>
      appStore.set((prev) => ({
        drawings: [...prev.drawings, drawing],
        scenes: prev.scenes.map((sc, i) => {
          if (i !== activeIdx) {
            return sc;
          }
          if (sc.visibleArrowIds.includes(id)) {
            return sc;
          }
          return { ...sc, visibleArrowIds: [...sc.visibleArrowIds, id] };
        }),
      })),
    undo: () =>
      appStore.set((prev) => ({
        drawings: prev.drawings.filter((d) => d.id !== id),
        selectedDrawingId: prev.selectedDrawingId === id ? null : prev.selectedDrawingId,
        scenes: prev.scenes.map((sc) =>
          sc.visibleArrowIds.includes(id)
            ? { ...sc, visibleArrowIds: sc.visibleArrowIds.filter((x) => x !== id) }
            : sc,
        ),
      })),
  };
}

/**
 * @param {string} drawingId
 * @returns {Command | null}
 */
export function deleteDrawingCommand(drawingId) {
  const state = appStore.get();
  const prev = state.drawings.find((d) => d.id === drawingId);
  if (!prev) {
    return null;
  }
  const snapshot = { ...prev };
  /** @type {boolean[]} */
  const visibilitySnapshot = state.scenes.map((sc) => sc.visibleArrowIds.includes(drawingId));
  return {
    type: "deleteDrawing",
    do: () =>
      appStore.set((s) => ({
        drawings: s.drawings.filter((d) => d.id !== drawingId),
        selectedDrawingId:
          s.selectedDrawingId === drawingId ? null : s.selectedDrawingId,
        scenes: s.scenes.map((sc) =>
          sc.visibleArrowIds.includes(drawingId)
            ? {
                ...sc,
                visibleArrowIds: sc.visibleArrowIds.filter((id) => id !== drawingId),
              }
            : sc,
        ),
      })),
    undo: () =>
      appStore.set((s) => ({
        drawings: [...s.drawings, snapshot],
        scenes: s.scenes.map((sc, i) => {
          const shouldRestore = visibilitySnapshot[i] ?? false;
          if (!shouldRestore || sc.visibleArrowIds.includes(drawingId)) {
            return sc;
          }
          return { ...sc, visibleArrowIds: [...sc.visibleArrowIds, drawingId] };
        }),
      })),
  };
}

/** @returns {Command | null} */
export function clearDrawingsCommand() {
  const state = appStore.get();
  const previous = state.drawings;
  if (previous.length === 0) {
    return null;
  }
  const snapshot = previous.map((d) => ({ ...d, points: d.points.map((p) => ({ ...p })) }));
  const visibilitySnapshot = state.scenes.map((sc) => [...sc.visibleArrowIds]);
  return {
    type: "clearDrawings",
    do: () =>
      appStore.set((s) => ({
        drawings: [],
        selectedDrawingId: null,
        scenes: s.scenes.map((sc) => ({ ...sc, visibleArrowIds: [] })),
      })),
    undo: () =>
      appStore.set((s) => ({
        drawings: snapshot,
        scenes: s.scenes.map((sc, i) => ({
          ...sc,
          visibleArrowIds: [...(visibilitySnapshot[i] ?? [])],
        })),
      })),
  };
}

/**
 * @param {ToolMode} from
 * @param {ToolMode} to
 * @returns {Command}
 */
export function setToolCommand(from, to) {
  return {
    type: "setTool",
    do: () => appStore.set({ tool: to, selectedDrawingId: null }),
    undo: () => appStore.set({ tool: from }),
  };
}

/**
 * @param {string | null} from
 * @param {string | null} to
 * @returns {Command}
 */
export function selectDrawingCommand(from, to) {
  return {
    type: "selectDrawing",
    do: () => appStore.set({ selectedDrawingId: to }),
    undo: () => appStore.set({ selectedDrawingId: from }),
  };
}

/**
 * @param {string} from
 * @param {string} to
 * @returns {Command}
 */
export function setColorCommand(from, to) {
  return {
    type: "setColor",
    do: () => appStore.set({ drawColor: to }),
    undo: () => appStore.set({ drawColor: from }),
  };
}

/**
 * @param {string} tokenId
 * @param {(t: Token) => Partial<Token>} mutator
 */
function updateToken(tokenId, mutator) {
  appStore.set((state) => ({
    tokens: state.tokens.map((t) => (t.id === tokenId ? { ...t, ...mutator(t) } : t)),
  }));
}
